import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { FaTrash, FaCheck, FaTimes, FaStar, FaFilter, FaSearch, FaRegCommentDots } from "react-icons/fa";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function Comments() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: "", status: "", fromDate: "", toDate: "" });

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllReviewsAdmin`);
      setReviews(res.data.reviews || []);
    } catch (err) {
      Swal.fire("Error", "Failed to fetch reviews", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${baseurl}updateReviewStatus/${id}`, { status: newStatus });
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Review ${newStatus}`, showConfirmButton: false, timer: 1500 });
      fetchReviews();
    } catch (err) { Swal.fire("Error", "Update failed", "error"); }
  };

  const deleteReview = async (id) => {
    const confirm = await Swal.fire({ title: "Delete?", icon: "warning", showCancelButton: true, confirmButtonColor: "#d33" });
    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteReview/${id}`);
        fetchReviews();
      } catch (err) { Swal.fire("Error", "Delete failed", "error"); }
    }
  };

  const filteredReviews = reviews.filter((r) => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = r.user_name?.toLowerCase().includes(searchLower) || r.product_name?.toLowerCase().includes(searchLower) || r.comment?.toLowerCase().includes(searchLower);
    const matchesStatus = filters.status ? r.status === filters.status : true;
    return matchesSearch && matchesStatus;
  });

  // Text Data Aggregation
  const stats = {
    total: filteredReviews.length,
    pending: filteredReviews.filter(r => r.status === 'pending').length,
    avgRating: (filteredReviews.reduce((acc, curr) => acc + curr.rating, 0) / (filteredReviews.length || 1)).toFixed(1)
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="main-content p-3">
          
          {/* Compact Header & Text Stats */}
          <div className="row g-3 mb-3 align-items-center">
            <div className="col-md-4">
              <h5 className="fw-bold mb-0">Reviews Ledger</h5>
              <small className="text-muted">Manage user feedback and ratings</small>
            </div>
            <div className="col-md-8">
              <div className="d-flex gap-2 justify-content-md-end">
                <div className="stat-pill"><strong>{stats.total}</strong> Total</div>
                <div className="stat-pill"><strong>{stats.pending}</strong> Pending</div>
                <div className="stat-pill text-warning"><strong>{stats.avgRating}</strong> <FaStar size={10}/> Avg</div>
              </div>
            </div>
          </div>

          {/* Ultra-Compact Filter Bar */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body p-2">
              <div className="row g-2 align-items-center">
                <div className="col-md-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-white border-end-0"><FaSearch className="text-muted"/></span>
                    <input type="text" className="form-control border-start-0" placeholder="Quick search..." value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} />
                  </div>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="col-md-4 d-flex gap-1">
                  <Flatpickr options={{ dateFormat: "Y-m-d" }} placeholder="From" className="form-control form-control-sm bg-white" onChange={(d) => setFilters({...filters, fromDate: d[0]})} />
                  <Flatpickr options={{ dateFormat: "Y-m-d" }} placeholder="To" className="form-control form-control-sm bg-white" onChange={(d) => setFilters({...filters, toDate: d[0]})} />
                </div>
                <div className="col-md-2 text-end">
                  <button className="btn btn-sm btn-primary w-100" onClick={fetchReviews}>Refresh</button>
                </div>
              </div>
            </div>
          </div>

          {/* Dense Table */}
          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0 custom-dense-table">
                <thead className="bg-white">
                  <tr>
                    <th className="ps-3">Reviewer</th>
                    <th>Product Context</th>
                    <th>Rating</th>
                    <th style={{width: '30%'}}>Commentary</th>
                    <th>Status</th>
                    <th className="text-end pe-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : filteredReviews.map((r) => (
                    <tr key={r.id}>
                      <td className="ps-3">
                        <div className="small fw-bold">{r.user_name}</div>
                        <div className="extra-small text-muted">{r.user_email}</div>
                      </td>
                      <td>
                        <div className="text-truncate small" style={{maxWidth: '150px'}} title={r.product_name}>
                          {r.product_name}
                        </div>
                        <div className="extra-small text-muted">{new Date(r.created_at).toLocaleDateString()}</div>
                      </td>
                      <td>
                        <div className="badge bg-light text-dark border fw-bold px-2 py-1">
                          {r.rating} <FaStar className="text-warning mb-1" size={10}/>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-start gap-2">
                          <FaRegCommentDots className="text-muted mt-1 flex-shrink-0" size={12}/>
                          <p className="extra-small mb-0 text-secondary lh-sm">{r.comment}</p>
                        </div>
                      </td>
                      <td>
                        <span className={`status-dot ${r.status}`}></span>
                        <span className="extra-small text-uppercase fw-bold">{r.status}</span>
                      </td>
                      <td className="text-end pe-3">
                        <div className="btn-group btn-group-sm shadow-sm border rounded">
                          {r.status !== "approved" && (
                            <button className="btn btn-white text-success border-0" onClick={() => updateStatus(r.id, "approved")}><FaCheck size={11}/></button>
                          )}
                          {r.status !== "rejected" && (
                            <button className="btn btn-white text-warning border-0" onClick={() => updateStatus(r.id, "rejected")}><FaTimes size={11}/></button>
                          )}
                          <button className="btn btn-white text-danger border-0" onClick={() => deleteReview(r.id)}><FaTrash size={11}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 11px; }
        .custom-dense-table thead th { font-size: 10px; text-transform: uppercase; color: #64748b; padding: 12px 8px; font-weight: 700; }
        .custom-dense-table tbody td { padding: 10px 8px; border-bottom: 1px solid #f1f5f9; }
        .stat-pill { background: white; padding: 4px 12px; border-radius: 20px; font-size: 12px; border: 1px solid #e2e8f0; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .status-dot { height: 8px; width: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; }
        .status-dot.approved { background-color: #10b981; }
        .status-dot.pending { background-color: #f59e0b; }
        .status-dot.rejected { background-color: #ef4444; }
        .btn-white { background: white; }
        .btn-white:hover { background: #f8fafc; }
      `}</style>
    </>
  );
}

export default Comments;